import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CustomerConst } from '@app/constants/customer-const';
import { MethodsHelper } from '@app/helpers/methods.helper';
import { CustomerFilter } from '@app/models/customer-filter';
import { KycField } from '@app/models/kyc-validation';
import { KycService } from '@app/services/kyc.service';

declare var $: any;

@Component({
    selector: 'app-customer-advanced-search-form',
    templateUrl: './advanced-search-form.component.html',
    styleUrls: ['./advanced-search-form.component.css'],
    standalone: false
})
export class AdvancedSearchFromComponent implements OnInit {

  customerFilter: CustomerFilter;

  @Input() displaySearchDialog: boolean;
  @Output() onSubmit = new EventEmitter<CustomerFilter>();
  @Output() onCancel = new EventEmitter();

  @ViewChild('form') formStep4: any;

  customerBirthDate: Date;
  submittedForm: boolean = false;

  selectedStatus: any;
  yearRange: string;
  emailMessage: string;

  statuses = [
    { 'value': 'ACTIVE', 'label': 'Active' },
    { 'value': 'INACTIVE', 'label': 'Inactive' },
    { 'value': 'READY_FOR_APPROVAL', 'label': 'Ready for Approval' },
    { 'value': 'APPROVED', 'label': 'Approved' },
    { 'value': 'WAITING_FOR_APPROVAL', 'label': 'Waiting for Approval' }
  ];
  genders = [
    { 'value': 'FEMALE', 'label': 'Female' },
    { 'value': 'MALE', 'label': 'Male' },
    { 'value': 'OTHER', 'label': 'Other' }
  ];

  readonly customerTypes = CustomerConst.CustomerTypes;

  documentTypeOptions: Array<{ value: string; label: string }> = [];
  isLoadingDocumentTypes = false;

  constructor(private kycService: KycService) { }

  ngOnInit() {
    this.yearRange = MethodsHelper.getYearRange(-1);
    const savedFilter = localStorage.getItem('customerAdvancedSearch');
    if (savedFilter) {
      this.customerFilter = Object.assign(new CustomerFilter(), JSON.parse(savedFilter));
    } else {
      this.customerFilter = new CustomerFilter();
    }
    if (!this.customerFilter.type) {
      this.customerFilter.type = CustomerConst.CUSTOMER_TYPE.INDIVIDUAL;
    }
    this.loadDocumentTypeOptions();
  }

  cancelSearch() {
    localStorage.removeItem('customerAdvancedSearch');
    this.onCancel.emit();
  }

  validateForm() {
    if (this.formStep4.form)
      return this.formStep4.form.valid;
    return false;
  }

  changeStatus(status) {
    this.customerFilter.status = status.value;
  }

  changeBirthDate($event) {
    this.customerFilter.birthDate = MethodsHelper.convertDateToString($event);
  }

  isIndividualType(): boolean {
    return !this.customerFilter.type || this.customerFilter.type === CustomerConst.CUSTOMER_TYPE.INDIVIDUAL;
  }

  showPersonalFields(): boolean {
    return this.isIndividualType() || this.customerFilter.type === CustomerConst.CUSTOMER_TYPE.PRIVATE_ENTREPRENEUR;
  }

  showCompanyNameField(): boolean {
    return !this.isIndividualType();
  }

  getCustomerTypeLabel(): string {
    return this.customerTypes.find(t => t.value === this.customerFilter.type)?.label || '';
  }

  getDocumentTypeLabel(): string {
    return this.documentTypeOptions.find(t => t.value === this.customerFilter.documentType)?.label || '';
  }

  onCustomerTypeChange() {
    if (!this.showPersonalFields()) {
      this.customerFilter.birthDate = undefined;
      this.customerBirthDate = undefined;
      this.customerFilter.gender = undefined;
      this.customerFilter.firstName = undefined;
      this.customerFilter.middleName = undefined;
      this.customerFilter.lastName = undefined;
    }
    if (this.isIndividualType()) {
      this.customerFilter.name = undefined;
    }
    this.customerFilter.documentType = undefined;
    this.customerFilter.documentNumber = undefined;
    this.loadDocumentTypeOptions();
  }

  private loadDocumentTypeOptions() {
    const customerType = this.customerFilter.type || CustomerConst.CUSTOMER_TYPE.INDIVIDUAL;
    this.isLoadingDocumentTypes = true;
    this.kycService.getValidationRules(customerType, 'documents').subscribe({
      next: (rules: KycField[]) => {
        const rulesByField = new Map(rules.map(rule => [rule.fieldInput, rule.dynamicValidation]));
        this.documentTypeOptions = CustomerConst.IdentityTypes
          .filter(identityType => identityType.value !== CustomerConst.IDENTITY_TYPE.SIGNATURE)
          .filter(identityType => {
            const rule = rulesByField.get('identityData' + identityType.value);
            return !!rule && rule.fieldRequirement !== 'HIDDEN';
          })
          .map(identityType => ({ value: identityType.value, label: 'customer_mngt.identityData' + identityType.value }));
        this.isLoadingDocumentTypes = false;
      },
      error: () => {
        this.documentTypeOptions = [];
        this.isLoadingDocumentTypes = false;
      }
    });
  }

  onSearch() {
    this.submittedForm = true;
    if (this.validateForm()) {
      localStorage.setItem('customerAdvancedSearch', JSON.stringify(this.customerFilter));
      this.onSubmit.emit({ ...this.customerFilter });
    }
  }

  numberOnly(event): boolean {
    const charCode = event.which ?? event.keyCode;
    return !(charCode != 46 && charCode > 31 && (charCode < 48 || charCode > 57))
  }

  allowAlphanumericSpace(event) {
    return MethodsHelper.onlyAlphanumericSpace(event);
  }

  isValidEmail() {
    const email = this.customerFilter.email;
    if (!email) {
      this.emailMessage = '';
    } else {
      const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      this.emailMessage = emailRegex.test(email) ? '' : 'Invalid email format';
    }
  }

}
